import { usePageViewEvent } from '@fs/zion-analytics'
import zionDebug from '@fs/zion-debug'
import { useHistory, useQueryParams } from '@fs/zion-router'
import { getAnonSession } from '@fs/zion-session'
import { imagePubliclyViewable, useSlsStateContext } from '@fs/zion-sls'
import { Skeleton } from '@fs/zion-ui'
import React, { useEffect, useRef, useState } from 'react'
import { getUser } from '@fs/zion-user'
import ExploreView from '../../../Lightyear/components/exploreView/ExploreView'
import { useSharedBrowserStateContext } from '../../providers/sharedBrowserProvider/SharedBrowserContext'
import { useR2DispatchContext } from '../../../RIP/providers/r2Provider/R2Context'
import { getGroupId } from '../../services/rms/rmsService'
import DebugViewer from '../../../RIP/views/debug/DebugViewer'
import HistoryViewer from '../../../RIP/views/history/components/HistoryViewer'
import NamesView from '../../../RIP/components/namesView/NamesView'

const DefaultImageView = React.lazy(() => import('../../../RIP/views/default/default'))
const CatalogViewer = React.lazy(() => import('../../../RIP/views/catalog/catalogViewer'))

const pageDetailMap = {
  index: 'RII: Indexing App',
  combo: 'RII: Combo Viewer App',
  explore: 'RII: Image Browser App',
  catalog: 'RII: Catalog Viewer App',
  fullText: 'Full Text',
}

const debug = zionDebug('siv:ViewManager') // localStorage.debug = "fs:siv:ViewManager"

export default function ViewManager({ imagePermission, validSession, setValidSession }) {
  const { query, setQuery } = useQueryParams()
  const zionSlsState = useSlsStateContext()
  // const sharedBrowserState = useSharedBrowserStateContext()
  const sendPageView = usePageViewEvent()
  const oldViewValue = useRef()
  const oldImageArk = useRef()
  const [groupId, setGroupId] = useState()
  const r2Dispatch = useR2DispatchContext()
  const sharedBrowserState = useSharedBrowserStateContext()

  const history = useHistory()
  const imageArk = history.location.pathname.split('/').pop()
  const isViewHistory = query?.view === 'history'

  useEffect(() => {
    async function checkSession() {
      if (imagePermission) {
        r2Dispatch({ type: 'SET_IMAGE_PERMISSION', value: imagePermission })
        let user
        try {
          user = await getUser()
        } catch (e) {
          debug('VM: getUser failed', e)
        }
        const publicallyViewable = imagePubliclyViewable(imagePermission)
        debug('VM: User information ', {
          validSession: Boolean(!publicallyViewable && user),
          publicallyViewable,
          userBool: Boolean(user),
          imagePermission,
        })
        r2Dispatch({ type: 'SET_LOAD_INDEXED_DATA', value: Boolean(user) })
        if (!user && (!publicallyViewable || isViewHistory)) {
          window.location.replace(`/auth/familysearch/login?returnUrl=${encodeURIComponent(window.location)}`)
          debug("VM: Redirecting to FamilySearch's login page")
          return
        }
        if (!user) {
          await getAnonSession()
          debug('VM: Anon session created')
        } else debug('VM: User session was good')
        setValidSession(true)
      }
    }
    checkSession()
  }, [imageArk, r2Dispatch, imagePermission, isViewHistory, setValidSession])

  useEffect(() => {
    async function checkGroupId() {
      const groupIdTemp = query?.groupId
      if (groupIdTemp) setGroupId(groupIdTemp)
      else if (!groupId) {
        const idArray = await getGroupId(imageArk)
        setGroupId(idArray?.[0])
      }
    }
    checkGroupId()
  }, [groupId, query?.groupId, imageArk])

  const { thumbnails } = sharedBrowserState

  if (
    // oldViewValue.current !== query?.view &&
    thumbnails?.length &&
    // sharedBrowserState?.focusedIndex > -1 &&
    imageArk !== oldImageArk.current &&
    groupId
  ) {
    sendPageView({
      page_detail: pageDetailMap?.[query.view],
      event_name: 'imageSearch_view',
      image_rmsId: thumbnails?.[sharedBrowserState?.focusedIndex]?.id,
      image_dgsId: groupId,
      volumeSetId: query.volumeSetSearch ? query.groupId : null,
    })
    oldViewValue.current = query?.view
    oldImageArk.current = imageArk
  }

  const indexExploreViews = ['index', 'fullText', 'explore', 'organize']
  const viewMap = {
    catalog: <CatalogViewer />,
    history: <HistoryViewer />,
    debug: <DebugViewer />,
  }

  debug('ViewManager: ', {
    validSession,
    query,
    zionSlsState,
  })

  // R2 will be the default view.
  useEffect(() => {
    if (!query?.view) {
      setQuery({ ...query, view: 'index' })
      debug('No view query param found. Setting default view to index.')
    }
  }, [])

  function renderView() {
    const { view = 'default' } = query || {}
    if (indexExploreViews.includes(view)) {
      return (
        <>
          {view === 'index' || view === 'fullText' ? (
            <NamesView validSession={validSession} />
          ) : (
            <ExploreView validSession={validSession} />
          )}
        </>
      )
    }

    if (viewMap[view]) return viewMap[view]
    return <DefaultImageView />
  }

  // We aren't using authRoute because of the contract with New Zealand to allow images to be viewed without logging in
  // So we simulate it ourselves
  if (validSession) return renderView()
  return <Skeleton.Image width="100%" height="100%" />
}
